import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import { Table } from "antd";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitGroupDto,
  DataKitSubgroupDto,
  GroupMenuUsageDto,
} from "../../../../domain/DataKitDto";
import "./DataKitGroupsTab.scss";

interface Props {
  kit: DataKitDto;
  groups: DataKitGroupDto[];
  subgroups: DataKitSubgroupDto[];
  canWrite: boolean;
  onChanged: () => void;
}

export function DataKitGroupsTab({ kit, groups, subgroups, canWrite, onChanged }: Props) {
  const [usage, setUsage] = useState<GroupMenuUsageDto[]>([]);
  const [editing, setEditing] = useState<DataKitGroupDto | null>(null);
  const [editName, setEditName] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);

  const loadUsage = useCallback(() => {
    if (!kit.id) return;
    DataKitApi.listGroupMenuUsage(kit.id)
      .then((res) => setUsage(res.data || []))
      .catch(() => undefined);
  }, [kit.id]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const menusByGroup = useMemo(() => {
    const map = new Map<string, string[]>();
    usage.forEach((u) => {
      const labels = (u.menuLabels || []).filter((l): l is string => !!l && l.trim() !== "");
      map.set(u.groupName, labels);
    });
    return map;
  }, [usage]);

  const openEdit = (group: DataKitGroupDto) => {
    setEditing(group);
    setEditName(group.name);
    setEditNote(group.note || "");
  };

  const save = async () => {
    if (!editing || !kit.id) return;
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    try {
      if (name !== editing.name) {
        await DataKitApi.renameGroup(kit.id, { name: editing.name, newName: name });
      }
      const note = editNote.trim();
      if (note !== (editing.note || "")) {
        await DataKitApi.setGroupNote(kit.id, { name, note: note || null });
      }
      setEditing(null);
      onChanged();
      loadUsage();
    } catch {
      message.error("Could not save the group");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Group",
      dataIndex: "name",
      key: "name",
      sorter: (a: DataKitGroupDto, b: DataKitGroupDto) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
      render: (_: string, group: DataKitGroupDto) => (
        <div className="data-kit-group-name-cell">
          <div className="data-kit-group-name">{group.name}</div>
          {group.note && <div className="data-kit-group-note">{group.note}</div>}
        </div>
      ),
    },
    {
      title: "Subgroups",
      key: "subgroups",
      width: 240,
      render: (_: unknown, group: DataKitGroupDto) => {
        const own = subgroups.filter((s) => s.groupName === group.name);
        if (own.length === 0) return <span className="data-kit-group-empty">--</span>;
        return (
          <div className="data-kit-subgroup-chips">
            {own.map((s) => (
              <span key={s.id || s.name} className="data-kit-subgroup-chip">
                {s.name}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Used on",
      key: "usedOn",
      width: 300,
      render: (_: unknown, group: DataKitGroupDto) => {
        const labels = menusByGroup.get(group.name) || [];
        if (labels.length === 0) return <span className="data-kit-group-empty">––</span>;
        return labels.join(" • ");
      },
    },
    {
      title: "",
      key: "action",
      width: 90,
      align: "right" as const,
      render: (_: unknown, group: DataKitGroupDto) =>
        canWrite ? (
          <Button className="data-kit-group-edit-btn" size="small" onClick={() => openEdit(group)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="data-kit-groups-tab">
      <p className="data-kit-groups-hint">
        Name + note live on the group — edits reach every design using it
      </p>

      <Table
        className="data-kit-groups-table"
        dataSource={groups}
        columns={columns}
        rowKey={(group) => group.id || group.name}
        pagination={false}
        locale={{ emptyText: "No groups yet" }}
      />

      <Modal
        className={"data-kit-group-modal clean one"}
        open={!!editing}
        width={520}
        title="Edit group"
        okText="Save"
        confirmLoading={saving}
        okButtonProps={{ disabled: !editName.trim() }}
        onOk={save}
        onCancel={() => setEditing(null)}
      >
        <label className="data-kit-field">
          <span>Name</span>
          <Input
            size="large"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            maxLength={255}
            autoFocus
          />
        </label>
        <label className="data-kit-field">
          <span>Note</span>
          <Input
            size="large"
            placeholder="e.g. all entrées served with choice of soup or salad"
            value={editNote}
            onChange={(e) => setEditNote(e.target.value)}
            maxLength={255}
          />
        </label>
      </Modal>
    </div>
  );
}
